import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { Phone as PhoneIcon } from 'lucide-react';
import { pbxApi, usersApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import TelephonyTab from '../../components/ems/exercise-detail/TelephonyTab';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  Phone, 
  Plus, 
  Trash2, 
  Edit, 
  Users, 
  User,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Voicemail,
  Clock,
  Circle,
  Settings
} from 'lucide-react';

export function PBXContent({ exerciseId }) {
  const { user } = useAuth();
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [huntGroups, setHuntGroups] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [callStats, setCallStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showAddNumber, setShowAddNumber] = useState(false);
  const [showAddHuntGroup, setShowAddHuntGroup] = useState(false);
  const [editingNumber, setEditingNumber] = useState(null);
  
  const [numberForm, setNumberForm] = useState({
    extension: '',
    display_number: '',
    role_name: '',
    voicemail_enabled: true,
  });
  
  const [huntGroupForm, setHuntGroupForm] = useState({
    name: '',
    ring_strategy: 'simultaneous',
    member_extensions: [],
    timeout_seconds: 30,
  });

  useEffect(() => {
    loadData();
  }, [exerciseId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [numbersData, groupsData, logsData, statsData, usersData] = await Promise.all([
        pbxApi.listNumbers(exerciseId),
        pbxApi.listHuntGroups(exerciseId),
        pbxApi.listCalls(exerciseId, 100),
        pbxApi.getCallStats(exerciseId),
        usersApi.getAll(),
      ]);
      
      setPhoneNumbers(numbersData);
      setHuntGroups(groupsData);
      setCallLogs(logsData);
      setCallStats(statsData);
      setUsers(usersData);
    } catch (error) {
      toast.error('Failed to load PBX data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    try {
      await pbxApi.bulkCreateNumbers(exerciseId, 10);
      toast.success('Phone numbers created');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create numbers');
    }
  };

  const handleAddNumber = async () => {
    try {
      await pbxApi.createNumber({
        exercise_id: exerciseId,
        ...numberForm,
      });
      toast.success('Phone number added');
      setShowAddNumber(false);
      setNumberForm({ extension: '', display_number: '', role_name: '', voicemail_enabled: true });
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to add number');
    }
  };

  const handleUpdateNumber = async () => {
    if (!editingNumber) return;
    try {
      await pbxApi.updateNumber(editingNumber.number_id, {
        role_name: numberForm.role_name,
        voicemail_enabled: numberForm.voicemail_enabled,
      });
      toast.success('Phone number updated');
      setEditingNumber(null);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update number');
    }
  };

  const handleDeleteNumber = async (numberId) => {
    if (!window.confirm('Delete this phone number?')) return;
    try {
      await pbxApi.deleteNumber(numberId);
      toast.success('Phone number deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete number');
    }
  };

  const handleAssignUser = async (numberId, userId) => {
    try {
      if (userId === 'unassigned') {
        await pbxApi.updateNumber(numberId, { assigned_user_id: null });
        toast.success('User unassigned');
      } else {
        await pbxApi.assignNumber(numberId, userId);
        toast.success('User assigned');
      }
      await loadData();
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  const handleAddHuntGroup = async () => {
    try {
      await pbxApi.createHuntGroup({
        exercise_id: exerciseId,
        ...huntGroupForm,
      });
      toast.success('Hunt group created');
      setShowAddHuntGroup(false);
      setHuntGroupForm({ name: '', ring_strategy: 'simultaneous', member_extensions: [], timeout_seconds: 30 });
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create hunt group');
    }
  };

  const handleDeleteHuntGroup = async (groupId) => {
    if (!window.confirm('Delete this hunt group?')) return;
    try {
      await pbxApi.deleteHuntGroup(groupId);
      toast.success('Hunt group deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete hunt group');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Phone className="w-6 h-6" />
              PBX Administration
            </h1>
            <p className="text-muted-foreground">Manage phone numbers, hunt groups, and call logs</p>
          </div>
          
          {phoneNumbers.length === 0 && (
            <Button onClick={handleBulkCreate} data-testid="bulk-create-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create 10 Numbers
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {callStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{callStats.total_calls}</div>
                <p className="text-xs text-muted-foreground">Total Calls</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-500">{callStats.completed_calls}</div>
                <p className="text-xs text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-500">{callStats.missed_calls}</div>
                <p className="text-xs text-muted-foreground">Missed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-orange-500">{callStats.voicemail_calls}</div>
                <p className="text-xs text-muted-foreground">Voicemails</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{formatDuration(callStats.average_duration)}</div>
                <p className="text-xs text-muted-foreground">Avg Duration</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="numbers">
          <TabsList>
            <TabsTrigger value="numbers">
              <Phone className="w-4 h-4 mr-1" />
              Numbers ({phoneNumbers.length}/20)
            </TabsTrigger>
            <TabsTrigger value="hunt-groups">
              <Users className="w-4 h-4 mr-1" />
              Hunt Groups ({huntGroups.length})
            </TabsTrigger>
            <TabsTrigger value="call-logs">
              <PhoneCall className="w-4 h-4 mr-1" />
              Call Logs ({callLogs.length})
            </TabsTrigger>
            <TabsTrigger value="telephony" data-testid="pbx-telephony-subtab">
              <Phone className="w-4 h-4 mr-1" />
              Telephony Routing
            </TabsTrigger>
          </TabsList>

          {/* Phone Numbers Tab */}
          <TabsContent value="numbers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Phone Numbers</CardTitle>
                  <CardDescription>Manage extensions and role assignments</CardDescription>
                </div>
                <Button onClick={() => setShowAddNumber(true)} disabled={phoneNumbers.length >= 20}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Number
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Extension</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Assigned User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Voicemail</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {phoneNumbers.map(number => (
                      <TableRow key={number.number_id}>
                        <TableCell className="font-mono font-bold">{number.extension}</TableCell>
                        <TableCell>{number.role_name}</TableCell>
                        <TableCell>
                          <Select
                            value={number.assigned_user_id || 'unassigned'}
                            onValueChange={(value) => handleAssignUser(number.number_id, value)}
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Assign user..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {users.map(u => (
                                <SelectItem key={u.user_id} value={u.user_id}>
                                  {u.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            number.status === 'available' ? 'text-green-500 border-green-500' :
                            number.status === 'busy' ? 'text-red-500 border-red-500' :
                            'text-muted-foreground border-gray-500'
                          }>
                            <Circle className="w-2 h-2 mr-1 fill-current" />
                            {number.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {number.voicemail_enabled ? (
                            <Voicemail className="w-4 h-4 text-green-500" />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingNumber(number);
                              setNumberForm({
                                extension: number.extension,
                                display_number: number.display_number,
                                role_name: number.role_name,
                                voicemail_enabled: number.voicemail_enabled,
                              });
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNumber(number.number_id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {phoneNumbers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No phone numbers yet. Click "Create 10 Numbers" to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hunt Groups Tab */}
          <TabsContent value="hunt-groups">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Hunt Groups</CardTitle>
                  <CardDescription>Ring multiple extensions simultaneously or in sequence</CardDescription>
                </div>
                <Button onClick={() => setShowAddHuntGroup(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Hunt Group
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {huntGroups.map(group => (
                    <div key={group.group_id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{group.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Strategy: {group.ring_strategy} | Timeout: {group.timeout_seconds}s
                        </p>
                        <div className="flex gap-1 mt-2">
                          {group.member_extensions.map(ext => (
                            <Badge key={ext} variant="secondary">{ext}</Badge>
                          ))}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteHuntGroup(group.group_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  {huntGroups.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hunt groups configured</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Call Logs Tab */}
          <TabsContent value="call-logs">
            <Card>
              <CardHeader>
                <CardTitle>Call Logs</CardTitle>
                <CardDescription>History of all calls in this exercise</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {callLogs.map(call => (
                        <TableRow key={call.call_id}>
                          <TableCell className="text-xs">
                            {new Date(call.started_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-mono">{call.from_extension}</span>
                              <p className="text-xs text-muted-foreground">{call.from_role}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-mono">{call.to_extension}</span>
                              <p className="text-xs text-muted-foreground">{call.to_role}</p>
                            </div>
                          </TableCell>
                          <TableCell>{formatDuration(call.duration_seconds)}</TableCell>
                          <TableCell>
                            <Badge variant={
                              call.status === 'completed' ? 'default' :
                              call.status === 'missed' ? 'destructive' : 'secondary'
                            }>
                              {call.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                      {callLogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            No calls logged yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Telephony Routing Tab — Telnyx multi-number config */}
          <TabsContent value="telephony">
            <TelephonyTab exerciseId={exerciseId} pbxNumbers={phoneNumbers} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Number Dialog */}
      <Dialog open={showAddNumber} onOpenChange={setShowAddNumber}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Extension</Label>
              <Input
                value={numberForm.extension}
                onChange={(e) => setNumberForm({...numberForm, extension: e.target.value})}
                placeholder="e.g., 101"
              />
            </div>
            <div className="space-y-2">
              <Label>Display Number</Label>
              <Input
                value={numberForm.display_number}
                onChange={(e) => setNumberForm({...numberForm, display_number: e.target.value})}
                placeholder="e.g., EX-101"
              />
            </div>
            <div className="space-y-2">
              <Label>Role Name</Label>
              <Input
                value={numberForm.role_name}
                onChange={(e) => setNumberForm({...numberForm, role_name: e.target.value})}
                placeholder="e.g., Incident Commander"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNumber(false)}>Cancel</Button>
            <Button onClick={handleAddNumber}>Add Number</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Number Dialog */}
      <Dialog open={!!editingNumber} onOpenChange={() => setEditingNumber(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Phone Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Extension</Label>
              <Input value={numberForm.extension} disabled />
            </div>
            <div className="space-y-2">
              <Label>Role Name</Label>
              <Input
                value={numberForm.role_name}
                onChange={(e) => setNumberForm({...numberForm, role_name: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNumber(null)}>Cancel</Button>
            <Button onClick={handleUpdateNumber}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Hunt Group Dialog */}
      <Dialog open={showAddHuntGroup} onOpenChange={setShowAddHuntGroup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Hunt Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input
                value={huntGroupForm.name}
                onChange={(e) => setHuntGroupForm({...huntGroupForm, name: e.target.value})}
                placeholder="e.g., Operations Team"
              />
            </div>
            <div className="space-y-2">
              <Label>Ring Strategy</Label>
              <Select
                value={huntGroupForm.ring_strategy}
                onValueChange={(v) => setHuntGroupForm({...huntGroupForm, ring_strategy: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simultaneous">Simultaneous (ring all)</SelectItem>
                  <SelectItem value="sequential">Sequential (one by one)</SelectItem>
                  <SelectItem value="random">Random</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Member Extensions</Label>
              <div className="flex flex-wrap gap-2">
                {phoneNumbers.map(number => (
                  <Button
                    key={number.extension}
                    variant={huntGroupForm.member_extensions.includes(number.extension) ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      const exts = huntGroupForm.member_extensions;
                      if (exts.includes(number.extension)) {
                        setHuntGroupForm({...huntGroupForm, member_extensions: exts.filter(e => e !== number.extension)});
                      } else {
                        setHuntGroupForm({...huntGroupForm, member_extensions: [...exts, number.extension]});
                      }
                    }}
                  >
                    {number.extension}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Timeout (seconds)</Label>
              <Input
                type="number"
                value={huntGroupForm.timeout_seconds}
                onChange={(e) => setHuntGroupForm({...huntGroupForm, timeout_seconds: parseInt(e.target.value)})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddHuntGroup(false)}>Cancel</Button>
            <Button onClick={handleAddHuntGroup}>Create Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PBXAdminPage() {
  const { exerciseId } = useParams();
  return (
    <DashboardLayout>
      <PBXContent exerciseId={exerciseId} />
    </DashboardLayout>
  );
}
